/**
 * Delivery Estimator — fecha estimada de entrega en la página de producto.
 *
 * Lee la config del metacampo tm_delivery.config (incrustada como JSON por
 * snippets/delivery-estimator.liquid) y calcula la fecha en el navegador.
 * Prioridad de reglas: vendor > primera colección con regla > ajustes generales.
 *
 * Misma lógica que lib/delivery/estimate.ts del dashboard — si cambias algo
 * aquí, cámbialo también allí. Los días usan numeración ISO (1=lunes..7=domingo)
 * y se opera con "fechas virtuales" (fecha local de la tienda como medianoche UTC).
 */
(function () {
  'use strict';

  var DAY_MS = 86400000;

  function isoDay(d) { return ((d.getUTCDay() + 6) % 7) + 1; }
  function addDays(d, n) { return new Date(d.getTime() + n * DAY_MS); }

  function nowInTimeZone(tz) {
    var parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
    }).formatToParts(new Date());
    var v = {};
    parts.forEach(function (p) { v[p.type] = p.value; });
    return {
      date: new Date(Date.UTC(+v.year, +v.month - 1, +v.day)),
      minutes: (+v.hour) * 60 + (+v.minute)
    };
  }

  function resolveSettings(data) {
    var config = data.config || {};
    var vendorKey = (data.vendor || '').trim().toLowerCase();
    if (vendorKey && config.vendors && config.vendors[vendorKey]) return config.vendors[vendorKey];
    var handles = data.collections || [];
    if (config.collections) {
      for (var i = 0; i < handles.length; i++) {
        if (config.collections[handles[i]]) return config.collections[handles[i]];
      }
    }
    return config['default'] || null;
  }

  function computeEstimate(s, tz) {
    var now = nowInTimeZone(tz);
    var cutoffParts = String(s.dispatch_time || '10:00').split(':');
    var cutoff = (+cutoffParts[0]) * 60 + (+cutoffParts[1] || 0);
    var dispatchDays = (s.dispatch_days && s.dispatch_days.length) ? s.dispatch_days : [1, 2, 3, 4, 5];
    var deliverableDays = (s.deliverable_days && s.deliverable_days.length) ? s.deliverable_days : [1, 2, 3, 4, 5];

    function isDispatchDay(d) { return dispatchDays.indexOf(isoDay(d)) !== -1; }
    function isDeliverableDay(d) { return deliverableDays.indexOf(isoDay(d)) !== -1; }

    // Día de salida: hoy si aún se llega al corte, si no el siguiente día de despacho
    var dispatch = now.date;
    var countdownMinutes = null;
    if (isDispatchDay(dispatch) && now.minutes < cutoff) {
      countdownMinutes = cutoff - now.minutes;
    } else {
      dispatch = addDays(dispatch, 1);
    }
    while (!isDispatchDay(dispatch)) dispatch = addDays(dispatch, 1);
    for (var i = 0; i < (s.delay_days || 0); i++) {
      dispatch = addDays(dispatch, 1);
      while (!isDispatchDay(dispatch)) dispatch = addDays(dispatch, 1);
    }

    // Tránsito: n días de reparto contados desde la salida
    function transit(days) {
      var d = dispatch;
      var n = 0;
      while (n < days) {
        d = addDays(d, 1);
        if (isDeliverableDay(d)) n++;
      }
      return d;
    }
    var minDays = Math.max(0, s.min_days || 0);
    var maxDays = s.range_enabled === false ? minDays : Math.max(minDays, s.max_days || 0);
    return { dispatch: dispatch, min: transit(minDays), max: transit(maxDays), countdownMinutes: countdownMinutes };
  }

  function formatDate(d, locale) {
    var text;
    try {
      text = new Intl.DateTimeFormat(locale, { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' }).format(d);
    } catch (e) {
      text = new Intl.DateTimeFormat('es', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' }).format(d);
    }
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.textContent = str == null ? '' : String(str);
    return div.innerHTML;
  }

  function render(el, data) {
    var settings = resolveSettings(data);
    if (!settings || settings.delivery_date_enabled === false) {
      el.setAttribute('hidden', '');
      return;
    }
    var tz = (data.config && data.config.timezone) || 'Europe/Madrid';
    var est = computeEstimate(settings, tz);
    var locale = data.locale || document.documentElement.lang || 'es';
    var texts = data.texts || {};

    var from = formatDate(est.min, locale);
    var to = formatDate(est.max, locale);
    var dateEl = el.querySelector('.delivery-estimator__date');
    if (est.max.getTime() > est.min.getTime()) {
      dateEl.innerHTML = escapeHtml(texts.range)
        .replace('[from]', '<strong>' + from + '</strong>')
        .replace('[to]', '<strong>' + to + '</strong>');
    } else {
      dateEl.innerHTML = escapeHtml(texts.single).replace('[date]', '<strong>' + from + '</strong>');
    }

    var cdEl = el.querySelector('.delivery-estimator__countdown');
    var cdText = el.querySelector('.delivery-estimator__countdown-text') || cdEl;
    if (data.showCountdown && est.countdownMinutes !== null) {
      var h = Math.floor(est.countdownMinutes / 60);
      var m = est.countdownMinutes % 60;
      var time = (h > 0 ? h + ' ' + texts.hours + ' ' : '') + m + ' ' + texts.minutes;
      cdText.innerHTML = escapeHtml(texts.countdown).replace('[time]', '<strong>' + time + '</strong>');
      cdEl.removeAttribute('hidden');
    } else {
      cdText.textContent = '';
      cdEl.setAttribute('hidden', '');
    }
    el.removeAttribute('hidden');
  }

  function init(el) {
    if (el.getAttribute('data-de-init')) return;
    el.setAttribute('data-de-init', '1');
    var script = el.querySelector('script[type="application/json"]');
    if (!script) return;
    var data;
    try { data = JSON.parse(script.textContent); } catch (e) { return; }
    if (!data || !data.config || data.config.enabled === false) return;
    var tick = function () {
      try { render(el, data); } catch (e) { el.setAttribute('hidden', ''); }
    };
    tick();
    setInterval(tick, 30000); // refresca el countdown y el cambio de día/corte
  }

  function initAll(root) {
    var scope = (root && root.querySelectorAll) ? root : document;
    var els = scope.querySelectorAll('delivery-estimator');
    for (var i = 0; i < els.length; i++) init(els[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  // Contenido inyectado dinámicamente (quick view, re-render de secciones)
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  new MutationObserver(function () { initAll(document); })
    .observe(document.documentElement, { childList: true, subtree: true });
})();
